import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { MapPin, Phone, Mail } from "lucide-react";
import { submitContactMessage } from "@/lib/booking.functions";
import { BUSINESS_SCHEMA, canonical } from "@/lib/seo";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact - Malak BnB Resort" },
      { name: "description", content: "Get in touch with Malak BnB Resort in Crystal Beach, Ontario. Reservations, events, and general inquiries." },
      { property: "og:title", content: "Contact - Malak BnB Resort" },
      { property: "og:description", content: "Reach us for reservations, events, and inquiries." },
      { property: "og:url", content: "https://malakbnbresort.ca/contact" },
      { property: "og:image", content: "/images/hero-exterior.webp" },
      { "script:ld+json": BUSINESS_SCHEMA },
    ],
    links: [canonical("/contact")],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const sendContact = useServerFn(submitContactMessage);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await sendContact({ data: form });
      setSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="bg-card py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl sm:text-5xl font-heading text-foreground animate-fade-in-up">Contact Us</h1>
          <p className="mt-4 text-muted-foreground max-w-xl">
            We'd love to hear from you. Reach out for reservations, event inquiries, or any questions about your stay.
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-2xl font-heading text-foreground mb-8">Get In Touch</h2>
              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <MapPin className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">Address</h3>
                    <p className="text-muted-foreground text-sm mt-1">
                      <a href={BUSINESS_SCHEMA.hasMap} target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
                        264 Ridge Road South, Crystal Beach<br />Ontario, Canada
                      </a>
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <Phone className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">Phone</h3>
                    <a href="tel:+12899298055" className="text-muted-foreground text-sm mt-1 hover:text-primary transition-colors">
                      1-289-929-8055
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-primary/10">
                    <Mail className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground text-sm">Email</h3>
                    <a href="mailto:infomalakbnb@gmail.com" className="text-muted-foreground text-sm mt-1 block hover:text-primary transition-colors">
                      infomalakbnb@gmail.com
                    </a>
                    <a href="mailto:lionroor@outlook.com" className="text-muted-foreground text-sm block hover:text-primary transition-colors">
                      lionroor@outlook.com
                    </a>
                  </div>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="mt-10 rounded-2xl overflow-hidden border border-border h-64 bg-muted flex items-center justify-center">
                <iframe
                  title="Malak BnB Resort location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2934.5!2d-79.0619!3d42.8661!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDLCsDUxJzU4LjAiTiA3OcKwMDMnNDMuMCJX!5e0!3m2!1sen!2sca!4v1700000000000"
                  className="w-full h-full border-0"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-heading text-foreground mb-8">Send Us a Message</h2>
              {submitted ? (
                <div className="rounded-2xl bg-primary/5 border border-primary/20 p-10 text-center">
                  <h3 className="text-xl font-heading text-foreground">Thank You!</h3>
                  <p className="mt-3 text-muted-foreground">We've received your message and will get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-1.5">Name</label>
                    <input
                      id="name"
                      type="text"
                      required
                      maxLength={100}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1.5">Email</label>
                    <input
                      id="email"
                      type="email"
                      required
                      maxLength={255}
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground mb-1.5">How Can We Help?</label>
                    <textarea
                      id="message"
                      required
                      maxLength={1000}
                      rows={5}
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      className="w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                      placeholder="Tell us about your inquiry..."
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-destructive">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full rounded-full bg-primary px-8 py-3.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending…" : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
